<template>
    <div class="privacy-policy">
        <!-- 头部 -->
        <header class="policy-header">
            <div class="container">
                <h1>Game Wonder Limited Privacy Policy</h1>
                <p class="last-updated"> Last Updated: April 22, 2026</p>
            </div>
        </header>
        <div class="container policy-container">
            <!-- 侧边栏目录导航 -->
            <aside class="policy-sidebar">
                <div class="sidebar-inner">
                    <h3>Contents</h3>
                    <ul class="nav-list">
                        <li v-for="section in sections" :key="section.id">
                            <a :href="`#${section.id}`" @click.prevent="scrollToSection(section.id)">
                                {{ section.title }}
                            </a>
                        </li>
                    </ul>
                </div>
            </aside>

            <!-- 主内容区 -->
            <main class="policy-content">
                <div class="content-card">
                    <!-- 1. Introduction -->
                    <section id="introduction" class="policy-section">
                        <h2>1. Introduction</h2>
                        <p>
                            Welcome to Game Wonder Limited (“we”, “us”, “our”). We respect and protect the personal
                            information and privacy of users who visit our website
                            (<a href="https://www.gamewonderads.com" target="_blank">https://www.gamewonderads.com</a>)
                            and use our one-stop cross-platform advertising management, agency, account opening,
                            recharge, data analysis, and related marketing technology services (collectively, the
                            “Services”).
                        </p>
                        <p>
                            This Privacy Policy explains how we collect, use, store, share, transfer, and protect your
                            personal information, as well as how you exercise your data rights. By accessing or using
                            our Services, you agree to the terms described in this policy.
                        </p>
                        <p>This policy applies to all users of the Services, including advertisers, merchants, partners,
                            and visitors.</p>
                    </section>

                    <!-- 2. Information We Collect -->
                    <section id="information-collect" class="policy-section">
                        <h2>2. Information We Collect</h2>
                        <p>We only collect information necessary to provide and secure the Services.</p>

                        <h3>2.1 Information You Provide</h3>
                        <ul>
                            <li><strong>Account registration information:</strong> name, company name, business email
                                address, mobile number, login password.</li>
                            <li><strong>Billing and payment information:</strong> billing address, bank or card
                                information, invoice information, and recharge records.</li>
                            <li><strong>Advertising campaign information:</strong> ad targeting settings, budget,
                                creatives, audience data, and campaign performance settings you submit for Facebook,
                                Google, TikTok, and other platforms.</li>
                            <li><strong>Customer service and communication data:</strong> information you provide when
                                contacting support, submitting feedback, or participating in marketing communications.
                            </li>
                        </ul>

                        <h3>2.2 Information Collected Automatically</h3>
                        <ul>
                            <li><strong>Device and usage data:</strong> IP address, browser type, operating system,
                                access time, pages viewed, click and interaction records, and crash logs.</li>
                            <li><strong>Advertising and business data:</strong> ad delivery status, conversion data,
                                ROI, CPA, and other statistics generated during use.</li>
                            <li><strong>Cookies and similar technologies:</strong> We use cookies to maintain login
                                status, optimize functions, analyze traffic, and improve services. You can manage cookie
                                preferences through your browser settings.</li>
                        </ul>

                        <h3>2.3 Information from Third Parties</h3>
                        <p>
                            With your consent or as required for service provision, we may obtain necessary advertising,
                            account, or settlement data from authorized platforms such as Facebook, Google, and TikTok.
                        </p>
                    </section>

                    <!-- 3. Purposes of Using Information -->
                    <section id="purposes" class="policy-section">
                        <h2>3. Purposes of Using Information</h2>
                        <p>We use your information only for the following legitimate purposes:</p>
                        <ol>
                            <li>To create, verify, and manage your user account.</li>
                            <li>To provide advertising agency, campaign operation, data analysis, and optimization
                                services.</li>
                            <li>To process recharge, payment, billing, and invoicing.</li>
                            <li>To provide customer support, respond to inquiries, and resolve issues.</li>
                            <li>To send service notifications, updates, security alerts, and invoices.</li>
                            <li>To improve system stability, security, and user experience.</li>
                            <li>To prevent fraud, abuse, illegal access, and other security risks.</li>
                            <li>To conduct compliance audits and meet legal or regulatory obligations.</li>
                            <li>With your consent, to send marketing information (you may opt out at any time).</li>
                        </ol>
                        <p>
                            We process personal information based on performance of a contract, legal obligation,
                            legitimate interest, or your explicit consent.
                        </p>
                    </section>

                    <!-- 4. Sharing and Disclosure -->
                    <section id="sharing" class="policy-section">
                        <h2>4. Sharing and Disclosure of Information</h2>
                        <p>We do not sell, rent, or illegally disclose your personal information. We may share
                            information only in the following situations:</p>

                        <h3>4.1 Authorized Service Providers</h3>
                        <ul>
                            <li>Payment processors, cloud service providers, data storage providers, and customer
                                service tools.</li>
                            <li>Such third parties are bound by confidentiality and data security obligations.</li>
                        </ul>

                        <h3>4.2 Advertising Platforms</h3>
                        <p>
                            To deliver and optimize your campaigns, we share necessary data with Facebook, Google,
                            TikTok,
                            and other authorized platforms, in line with their applicable terms and privacy rules.
                        </p>

                        <h3>4.3 Legal Requirements</h3>
                        <p>
                            We may disclose information to comply with laws, regulations, court orders, or requests
                            from competent governmental authorities.
                        </p>

                        <h3>4.4 Mergers, Acquisitions, or Transfers</h3>
                        <p>
                            In the event of restructuring, merger, acquisition, or asset sale, personal information
                            may be transferred under applicable laws and with appropriate safeguards.
                        </p>
                    </section>

                    <!-- 5. Payment Card Security -->
                    <section id="payment-security" class="policy-section">
                        <h2>5. Payment Card Security and PCI DSS Compliance</h2>
                        <p>
                            We are committed to protecting cardholder data and comply with the Payment Card Industry
                            Data Security Standard (PCI DSS).
                        </p>
                        <p>
                            We acknowledge that we are responsible for the security of cardholder data that we store,
                            process, or transmit on behalf of our customers or to the extent that we could impact the
                            security of the customer's Cardholder Data Environment (CDE). We further confirm that we
                            implement and maintain appropriate security measures in accordance with PCI DSS requirements
                            applicable to our services and scope of responsibility.
                        </p>
                        <p>In addition to the above, we adhere to the following security practices:</p>
                        <ul>
                            <li>We do not store sensitive authentication data such as CVV.</li>
                            <li>All card data transmission uses encryption (SSL/TLS).</li>
                            <li>Access to cardholder data is strictly limited to authorized personnel.</li>
                            <li>We maintain secure systems and regular security assessments to protect the Cardholder
                                Data Environment (CDE).</li>
                        </ul>
                    </section>

                    <!-- 6. Data Security -->
                    <section id="data-security" class="policy-section">
                        <h2>6. Data Security</h2>
                        <p>We adopt industry-standard administrative, technical, and physical safeguards:</p>
                        <ul>
                            <li>Encryption during transmission and storage.</li>
                            <li>Strict access control and identity verification.</li>
                            <li>Regular security testing, vulnerability scanning, and log auditing.</li>
                            <li>Emergency response mechanisms for data breaches.</li>
                        </ul>
                        <p>
                            While we take all reasonable steps to protect data, no Internet transmission or electronic
                            storage is 100% secure. We will notify users and regulators in accordance with laws in the
                            event of a confirmed data breach.
                        </p>
                    </section>

                    <!-- 7. Data Retention -->
                    <section id="data-retention" class="policy-section">
                        <h2>7. Data Retention</h2>
                        <p>We retain personal information only for as long as necessary to fulfill the purposes stated:
                        </p>
                        <ul>
                            <li><strong>Account information:</strong> retained until account cancellation plus a legally
                                required period.</li>
                            <li><strong>Transaction and billing data:</strong> retained as required by tax and financial
                                regulations.</li>
                            <li><strong>Usage and log data:</strong> retained for a maximum of 90–180 days (subject to
                                security needs).</li>
                            <li><strong>Advertising data:</strong> retained as required for campaign settlement and
                                compliance.</li>
                        </ul>
                        <p>When no longer needed, data is securely deleted or anonymized.</p>
                    </section>

                    <!-- 8. International Data Transfers -->
                    <section id="international-transfers" class="policy-section">
                        <h2>8. International Data Transfers</h2>
                        <p>
                            As a cross-border service provider, your information may be transferred to and processed
                            in other countries/regions. We ensure such transfers comply with applicable laws by
                            implementing:
                        </p>
                        <ul>
                            <li>Standard Contractual Clauses (SCCs)</li>
                            <li>Other appropriate safeguards approved by relevant data protection authorities</li>
                        </ul>
                    </section>

                    <!-- 9. Your Data Rights -->
                    <section id="data-rights" class="policy-section">
                        <h2>9. Your Data Rights</h2>
                        <p>Depending on your jurisdiction, you may exercise the following rights:</p>
                        <ol>
                            <li><strong>Access:</strong> Request details of personal information we hold about you.</li>
                            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                            <li><strong>Deletion:</strong> Request deletion of your personal information (where legally
                                permissible).</li>
                            <li><strong>Restriction or objection:</strong> Object to or restrict certain processing.
                            </li>
                            <li><strong>Data portability:</strong> Receive your data in a structured, machine-readable
                                format.</li>
                            <li><strong>Withdraw consent:</strong> Withdraw previously given consent at any time.</li>
                        </ol>
                        <p>
                            To exercise these rights, contact us at:
                            <a href="mailto:gamewonderads@outlook.com">gamewonderads@outlook.com</a>.
                            We will respond to valid requests within 30 days and verify your identity before processing.
                        </p>
                    </section>

                    <!-- 10. Marketing Communications -->
                    <section id="marketing" class="policy-section">
                        <h2>10. Marketing Communications</h2>
                        <p>If you receive marketing emails or messages, you may opt out by:</p>
                        <ul>
                            <li>Clicking the unsubscribe link in emails</li>
                            <li>Replying with “unsubscribe”</li>
                            <li>Adjusting settings in your account</li>
                        </ul>
                    </section>

                    <!-- 11. Cookie Policy -->
                    <section id="cookies" class="policy-section">
                        <h2>11. Cookie Policy</h2>
                        <p>We use cookies and similar technologies to:</p>
                        <ul>
                            <li>Maintain login status</li>
                            <li>Analyze website traffic and usage</li>
                            <li>Optimize advertising and user experience</li>
                        </ul>
                        <p>You may disable cookies via browser settings; however, some functions may be affected.</p>
                    </section>

                    <!-- 12. Minor Protection -->
                    <section id="minor-protection" class="policy-section">
                        <h2>12. Minor Protection</h2>
                        <p>
                            Our Services are intended for business users and are not directed at children under the age
                            of 13.
                            If we become aware that we have inadvertently collected data from a minor, we will delete it
                            promptly.
                        </p>
                    </section>

                    <!-- 13. Changes to This Privacy Policy -->
                    <section id="changes" class="policy-section">
                        <h2>13. Changes to This Privacy Policy</h2>
                        <p>
                            We may update this policy to reflect legal, technical, or business changes. The revised
                            version
                            will be posted on this page with an updated “Last Updated” date. Material changes will be
                            notified via website notice or email.
                        </p>
                    </section>

                    <!-- 14. Contact Us -->
                    <section id="contact" class="policy-section">
                        <h2>14. Contact Us</h2>
                        <p>
                            If you have questions, complaints, or requests regarding this Privacy Policy, please contact
                            us:
                        </p>
                        <div class="contact-info">
                            <p><strong>Game Wonder Limited</strong></p>
                            <p>Email: <a href="mailto:gamewonderads@outlook.com">gamewonderads@outlook.com</a></p>
                        </div>
                    </section>

                    <!-- 返回顶部按钮 -->
                    <button v-show="showBackToTop" @click="scrollToTop" class="back-to-top">
                        ↑ Back to Top
                    </button>
                </div>
            </main>
        </div>
    </div>
</template>

<script>
export default {
    name: "PrivacyPolicy",
    data() {
        return {
            showBackToTop: false,
            sections: [
                { id: "introduction", title: "1. Introduction" },
                { id: "information-collect", title: "2. Information We Collect" },
                { id: "purposes", title: "3. Purposes of Using Information" },
                { id: "sharing", title: "4. Sharing and Disclosure" },
                { id: "payment-security", title: "5. Payment Card Security" },
                { id: "data-security", title: "6. Data Security" },
                { id: "data-retention", title: "7. Data Retention" },
                { id: "international-transfers", title: "8. International Data Transfers" },
                { id: "data-rights", title: "9. Your Data Rights" },
                { id: "marketing", title: "10. Marketing Communications" },
                { id: "cookies", title: "11. Cookie Policy" },
                { id: "minor-protection", title: "12. Minor Protection" },
                { id: "changes", title: "13. Changes to This Privacy Policy" },
                { id: "contact", title: "14. Contact Us" }
            ]
        };
    },
    mounted() {
        window.addEventListener("scroll", this.handleScroll);
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.handleScroll);
    },
    methods: {
        scrollToSection(sectionId) {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
                // 更新 URL hash
                history.pushState(null, null, `#${sectionId}`);
            }
        },
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        },
        handleScroll() {
            this.showBackToTop = window.scrollY > 300;
        }
    }
};
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-size: 16px;
}

.privacy-policy {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    background-color: #f5f7fa;
    color: #1a1a2e;
    line-height: 1.6;
}

.container {
    max-width: 100vw;
    margin: 0 auto;
    padding: 0 24px;
}

/* 头部样式 */
.policy-header {
    padding: 40px 0;
    text-align: center;
}

.policy-header h1 {
    font-size: 2.5rem;
    font-weight: 600;
}

.last-updated {
    font-size: 2rem;
    opacity: 0.8;
}

/* 双栏布局 */
.policy-container {
    display: flex;
    gap: 40px;
    padding-top: 48px;
    padding-bottom: 80px;
}

/* 侧边栏 */
.policy-sidebar {
    width: 280px;
    flex-shrink: 0;
}

.sidebar-inner {
    position: sticky;
    top: 24px;
    background: white;
    border-radius: 16px;
    padding: 24px 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.sidebar-inner h3 {
    /* font-size: 1.1rem; */
    font-weight: 600;
    color: #1a1a2e;
    padding-bottom: 16px;
    border-bottom: 2px solid #e0e4e8;
    margin-bottom: 16px;
}

.nav-list {
    list-style: none;
}

.nav-list li {
    margin-bottom: 12px;
}

.nav-list li a {
    display: block;
    padding: 6px 0;
    color: #4a5568;
    text-decoration: none;
    /* font-size: 0.85rem; */
    transition: all 0.2s ease;
    border-left: 2px solid transparent;
    padding-left: 12px;
}

.nav-list li a:hover {
    color: #2c7da0;
    border-left-color: #2c7da0;
}

/* 主内容区 */
.policy-content {
    flex: 1;
    min-width: 0;
}

.content-card {
    background: white;
    border-radius: 24px;
    padding: 48px 56px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

/* 章节样式 */
.policy-section {
    margin-bottom: 48px;
    scroll-margin-top: 80px;
}

.policy-section:last-child {
    margin-bottom: 0;
}

.policy-section h2 {
    font-size: 2rem;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e9ecef;
}

.policy-section h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    margin: 24px 0 12px 0;
}

.policy-section p {
    margin-bottom: 16px;
    color: #2d3748;
}

.policy-section ul,
.policy-section ol {
    margin: 12px 0 16px 32px;
    color: #2d3748;
}

.policy-section li {
    margin-bottom: 8px;
}

.policy-section a {
    color: #2c7da0;
    text-decoration: none;
    transition: color 0.2s;
}

.policy-section a:hover {
    color: #1f5e7a;
    text-decoration: underline;
}

.contact-info {
    background: #f8f9fa;
    padding: 20px 24px;
    border-radius: 12px;
    margin-top: 16px;
}

.contact-info p {
    margin-bottom: 8px;
}

/* 返回顶部按钮 */
.back-to-top {
    position: fixed;
    bottom: 40px;
    right: 40px;
    background: #00a7f5;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 40px;
    cursor: pointer;
    /* font-size: 14px; */
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    z-index: 100;
}

.back-to-top:hover {
    background: #1f5e7a;
    transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 1024px) {
    .policy-container {
        flex-direction: column;
    }

    .policy-sidebar {
        width: 100%;
        position: static;
    }

    .sidebar-inner {
        position: static;
    }

    .nav-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 16px;
    }

    .nav-list li {
        margin-bottom: 0;
    }

    .nav-list li a {
        display: inline-block;
        border-left: none;
        padding: 4px 12px;
        background: #f0f2f5;
        border-radius: 20px;
        /* font-size: 0.8rem; */
    }

    .nav-list li a:hover {
        border-left-color: transparent;
        background: #2c7da0;
        color: white;
    }

    .content-card {
        padding: 32px 24px;
    }
}

@media (max-width: 640px) {
    .policy-header {
        padding: 40px 0;
    }

    .policy-header h1 {
        /* font-size: 1.8rem; */
    }

    .content-card {
        padding: 24px 16px;
    }

    .policy-section h2 {
        /* font-size: 1.4rem; */
    }

    .policy-section h3 {
        /* font-size: 1.1rem; */
    }

    .back-to-top {
        bottom: 20px;
        right: 20px;
        padding: 10px 16px;
        /* font-size: 12px; */
    }
}
</style>